import i18next from 'i18next';
import { toTitleCase } from 'utility/functions';
import { TWidget, TWidgetData } from 'utility/types/dataProvider';
import {
  formatChartValue,
  getChartConfig,
  getDatapointFormat,
} from 'components/Dashboard/shared/utils';

/**
 * CMSADMIN-1196
 * Contract with the api:
 * Stacked bar chart data (TSourceViews[]) is an array of objects with the following structure:
 *   label: string; // The label of the data point (e.g. "Direct", "Organic", etc.)
 *   value: number[]; // The array of values associated with the label (e.g. [100)
 */
type TStackedValues = {
  label: string;
  value: number;
};

// Specifically for the editorial.viewsbytrafficsource widget.
type TOldStackedValues = {
  source: string;
  views: number;
};

/**
 * Helper function to check if a stacked value uses the old data schema.
 */
const isOldStackedValue = (
  value: TStackedValues | TOldStackedValues,
): value is TOldStackedValues => {
  return 'source' in value;
};

/**
 * Extracts unique labels from an array of stacked bar chart data.
 * Handles both old and new data schemas.
 */
const extractUniqueLabels = (
  viewsArray: (TStackedValues[] | TOldStackedValues[])[],
): string[] => {
  const labelSet = new Set<string>();
  viewsArray.forEach((viewArr) => {
    if (Array.isArray(viewArr)) {
      viewArr.forEach((stackedValue: TStackedValues | TOldStackedValues) => {
        const label = isOldStackedValue(stackedValue)
          ? stackedValue.source
          : stackedValue.label;
        labelSet.add(label);
      });
    }
  });
  return Array.from(labelSet);
};

export const parseStackedBarChartData = (data: TWidgetData, widget: TWidget) => {
  if (!widget) return;
  if (!data?.results?.length) {
    return {
      isHorizontal: false,
      series: [],
      xAxis: [],
      dataset: [],
    };
  }

  // Get the locale from i18next.
  const locale = i18next.language;
  const dataset = data?.results;
  const { config } = getChartConfig(widget);

  // Y-Axis datapoint (viewsArray--the data that will be stacked).
  const yKey = config?.['y-col'];
  const viewsArray = dataset.map((item) => item?.[yKey]);

  // Collect all unique labels across all time buckets
  const labels = extractUniqueLabels(viewsArray);

  // =========================================
  // Y-Axis
  // =========================================
  // Create an object to store the data for each source (label).
  const labelValueMapping: Record<string, TStackedValues['value'][]> = Object.fromEntries(
    labels.map((source: any) => [source, []]),
  );

  // Create the data for each source.
  labels.forEach((label: string) => {
    // Map the data to the sourceKey.
    labelValueMapping[label] = viewsArray.map(
      (stackedArray: TStackedValues[] | TOldStackedValues[]) => {
        const found = stackedArray?.find((item: TStackedValues | TOldStackedValues) => {
          const itemLabel = isOldStackedValue(item) ? item.source : item.label;
          return itemLabel === label;
        });

        if (!found) return 0;
        return isOldStackedValue(found) ? found.views : found.value;
      },
    );
  });

  const yAxis = labels?.map((source: string) => {
    return {
      data: labelValueMapping[source],
      label: source === '(direct)' ? 'Direct' : toTitleCase(source),
      stack: 'stack1',
      valueFormatter: (value: any) => {
        // Pass datapoint config for the yKey to get the correct formatting (e.g. number, currency, etc.).
        const format = getDatapointFormat(widget.module?.data?.[yKey]);
        return formatChartValue({
          value,
          format: format || 'number',
          locale,
        });
      },
    };
  });

  // =========================================
  // X-Axis
  // =========================================
  const xKey = config?.['x-col'];
  const xDateFormat = config?.['x-display-format'] || 'g a';
  const xLabel = widget.module?.data?.[xKey]?.label || toTitleCase(xKey);
  const xTimes = dataset?.map((item: any) => item?.[xKey || 'utc_hour']);

  // Form the xAxis (x-col data refs).
  const xAxis = [
    {
      data: xTimes,
      label: xLabel,
      valueFormatter: (value: any) => {
        return formatChartValue({
          value,
          format: 'date',
          dateFormat: xDateFormat,
          locale,
        });
      },
      scaleType: 'band' as const,
    },
  ];

  // Return the parsed data.
  return {
    dataset,
    xAxis,
    series: yAxis,
  };
};

export const parseBarChartData = (data: TWidgetData, widget: TWidget) => {
  if (!widget) return;
  if (!data?.results?.length) {
    return {
      isHorizontal: false,
      series: [],
      xAxis: [],
      dataset: [],
    };
  }

  const locale = i18next.language;

  // Get the module/configuration.
  const {
    isStacked,
    isHorizontal,
    xAxisDisplayFormat,
    yDataKeys,
    yAxisLabels,
    yColumnLabelAndFields,
    xDataKeys,
  } = parseBarChartConfig(widget);

  // Check if x-axis is a date.
  const datapointConfig = widget.module?.data?.[xDataKeys[0]];
  const xFormat = getDatapointFormat(datapointConfig, xAxisDisplayFormat);

  // Sorting the dataset if the x-axis is not a date.
  let dataset =
    xFormat === 'date'
      ? data?.results
      : data?.results.sort((a: any, b: any) => {
          const filterKey = xDataKeys[0];

          // Sort the data in ascending order.
          return a[filterKey] - b[filterKey];
        });

  // Filter out duplicate rows where all y-values are 0
  // The API sometimes returns duplicate date entries with zero values
  if (isStacked) {
    dataset = dataset.filter((item: any) => {
      // Check if all y-values are non-zero (at least one should have data)
      return yDataKeys.some((key: string) => {
        const value = item[key];
        return value != null && value !== 0;
      });
    });
  }

  // =========================================
  // Y-Axis
  // =========================================

  // Y-Axis data.
  const yAxis = yDataKeys?.map((dataKey: string) => {
    const current = widget.module?.data?.[dataKey];
    const format = getDatapointFormat(current);
    const label = current?.label;

    const isStringFormatter = dataKey === 'device' || current?.type === 'string';

    // Pass raw values and let valueFormatter handle display
    const data = dataset.map((item: any) => item[dataKey]);

    // Value formatter.
    let valueFormatter = (value: any) =>
      formatChartValue({ value, format, dateFormat: current?.date_format, locale });

    // Special case for device.
    if (isStringFormatter) {
      valueFormatter = (value = '') => toTitleCase(value.toString());
    }

    // Return as xAxis.
    if (isHorizontal) {
      return {
        id: `bar-y-axis-horizontal--${widget.id}-${dataKey}`,
        data,
        valueFormatter,
        scaleType: 'band' as const,
        format,
        current,
      };
    }

    return {
      id: `bar-y-axis--${widget.id}-${dataKey}`,
      data,
      label,
      valueFormatter,
      stack: isStacked ? `${yColumnLabelAndFields?.label}` : undefined,
    };
  });

  // Assemble the settings, just labels.
  const settings = {
    yAxis: yAxisLabels.map((label: string) => ({ label })),
  };

  // =========================================
  // X-Axis
  // =========================================

  // Form the xAxis (x-col data refs).
  const xAxis = xDataKeys.map((dataKey: string) => {
    const current = widget.module?.data?.[dataKey];
    const axisLabel = current?.label;
    const format = getDatapointFormat(current, xAxisDisplayFormat);
    const dateFormat = xAxisDisplayFormat ?? current?.date_format;

    const valueFormatter = (value: any) =>
      formatChartValue({ value, format, dateFormat, locale });

    // Pass raw values and let valueFormatter handle display
    const data = dataset.map((item: any) => item[dataKey]);

    // Return as series.
    if (isHorizontal) {
      return {
        id: `bar-x-axis-horizontal--${widget.id}-${dataKey}`,
        data,
        valueFormatter,
        stack: isStacked ? `${yColumnLabelAndFields?.label}` : undefined,
        format,
        current,
      };
    }

    return {
      id: `bar-x-axis--${widget.id}-${dataKey}`,
      data,
      label: axisLabel,
      valueFormatter,
      scaleType: 'band' as const,
    };
  });

  return {
    isHorizontal,
    dataset,
    xAxis,
    series: yAxis,
    settings,
  };
};

export const parseBarChartConfig = (widget: TWidget) => {
  const { config } = getChartConfig(widget);

  // Determine if the chart is stacked.
  const isStacked = config?.subtype === 'stacked';

  // Determine if the chart is horizontal.
  const horizontalBarChartWidgetIds = ['editorial.devices', 'editorial.assetdevices'];
  const isHorizontal =
    config?.direction === 'horizontal' || horizontalBarChartWidgetIds.includes(widget.id);

  // =========================================
  // Y-Axis.
  // =========================================
  const yAxisConfig = Array.isArray(config?.['y-col'])
    ? config?.['y-col']
    : [config?.['y-col']];

  // Labels for all items along the y-axis.
  const yAxisLabels = yAxisConfig.map((key: string) => widget.module?.data?.[key]?.label);

  // This can also be a string, if not stacked.
  let yColumnLabelAndFields: { label: string; fields: string[] } | string =
    config?.['y-col'];

  // Compensate for string type.
  if (typeof yColumnLabelAndFields === 'string') {
    yColumnLabelAndFields = {
      label: widget.module?.data?.[yColumnLabelAndFields]?.label,
      fields: [yColumnLabelAndFields],
    };
  }

  const yDataKeys = Array.isArray(yColumnLabelAndFields?.fields)
    ? yColumnLabelAndFields?.fields
    : [yColumnLabelAndFields?.fields];

  // =========================================
  // X-Axis.
  // =========================================

  // Display format for the x-axis.
  const xAxisDisplayFormat = config?.['x-display-format'];

  const xDataKeys: string[] = Array.isArray(config?.['x-col'])
    ? config?.['x-col']
    : [config?.['x-col']];

  // Return the parsed configuration.
  return {
    config,
    isStacked,
    isHorizontal,
    xAxisDisplayFormat,
    yDataKeys,
    yAxisLabels,
    yColumnLabelAndFields,
    xDataKeys,
  };
};

/**
 * CMSADMIN-1196: accommodation for old shape of stacked bar chart data.
 * Check if the chart is stacked and if the data for any of the yDataKeys is an array.
 * @param data - The data object containing the results.
 * @param isStacked - A boolean indicating if the chart is stacked.
 * @param yDataKeys - An array of yDataKeys to check.
 * @returns boolean
 */
export const getIsOldSchoolStacked = ({
  data,
  isStacked,
  yDataKeys,
}: {
  data: TWidgetData;
  isStacked: boolean;
  yDataKeys: string[];
}) => {
  if (!isStacked || !yDataKeys?.length || !data?.results?.length) return false;

  // const { isStacked, yDataKeys } = parseBarChartConfig(widget);
  const firstDatapoint = data?.results?.[0] || {};

  // Check if the data for any of the yDataKeys is an array.
  const hasArrayData = yDataKeys.some((key: string) =>
    Array.isArray(firstDatapoint?.[key]),
  );

  return isStacked && !hasArrayData;
};
